import React, { useEffect, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import type { AgreementsFormProps } from '@features/agreements';
import type { CreatePasswordFormProps } from '@features/index';

import type { AgentRegistrationFormProps } from '@entities/agent-registration';
import type { CreateImportedAgentRequestParams } from '@entities/agent-user';
import { useCreateImportedAgent } from '@entities/agent-user';
import { useHandleRegistrationCodeErrors } from '@entities/registration';

import {
  AGENT_NOT_FOUND,
  AnalyticEvent,
  FlkCode,
  OTP,
} from '@shared/constants';
import { useStores } from '@shared/hooks';
import { FullWidthPrimaryButton } from '@shared/ui';
import {
  addTestAttribute,
  getConsents,
  phoneValueCasting,
} from '@shared/utils';

export const SubmitAgentRegistration = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    MainStore: {
      agentUserStore: { setAgent, setConfirmToken },
    },
  } = useStores();

  const [requestParams, setRequestParams] =
    useState<CreateImportedAgentRequestParams>({
      agent: {
        phone: '',
        password: '',
      },
      consents: [],
    });

  const { res, refetch, error, isFetching } = useCreateImportedAgent(
    {
      ...requestParams,
    },
    [requestParams]
  );

  const {
    handleSubmit,
    setError,
    formState: { errors },
  } = useFormContext<
    AgentRegistrationFormProps & CreatePasswordFormProps & AgreementsFormProps
  >();

  useEffect(() => {
    if (!!requestParams.agent.phone) {
      refetch();
    }
  }, [requestParams]);

  useEffect(() => {
    if (!isFetching && res) {
      setAgent(res.agent);
      setConfirmToken(res.token);
      sendAnalyticEvent(AnalyticEvent.AGENT_REG_TO_STEP_OTP);
      navigate(OTP);
    }
  }, [isFetching, res]);

  const handleSpecificCodeErrors = useMemo(
    () => ({
      // Агент не найден
      [FlkCode.AGT_4701_0002]: () => {
        navigate(AGENT_NOT_FOUND);
      },
    }),
    []
  );

  useHandleRegistrationCodeErrors({
    error,
    setError,
    handleSpecificCodeErrors,
  });

  const handleFormSubmit = handleSubmit((values) => {
    const { phone, personalDataAgreement, specialOffersAgreement, password } =
      values;

    setRequestParams({
      agent: {
        phone: phoneValueCasting(phone),
        password,
      },
      consents: getConsents({
        personalDataAgreement,
        specialOffersAgreement,
      }),
    });
  });

  return (
    <FullWidthPrimaryButton
      size='l'
      onClick={handleFormSubmit}
      disabled={!!Object.keys(errors).length}
      isLoading={isFetching}
      {...addTestAttribute('registration.button.submit')}
    >
      {t('COMMON:buttons.continue')}
    </FullWidthPrimaryButton>
  );
});
